import { keepPreviousData, useInfiniteQuery, type UseInfiniteQueryResult, type InfiniteData } from "@tanstack/react-query";
import type { Read } from "@/components/ui/box.schema";
import { historyCopy } from "@/components/history/history-copy";
import { importsApi, type ImportBatch, type ImportBatchQuery } from "@/lib/api/imports";

export type RunsFilter = "apply" | "dry_run" | "all";
export type KindFilter = "all" | "historical" | "registrar_roster";

/** Runs read per page. A full page is the only sign that older runs remain. */
export const RUN_PAGE = 100;

/** The server query a filter and a find ask for; "all" asks for no narrowing. */
export function runQuery(runs: RunsFilter, kind: KindFilter, q: string): ImportBatchQuery {
  const needle = q.trim();
  return { ...(runs === "all" ? {} : { mode: runs }), ...(kind === "all" ? {} : { kind }), ...(needle ? { q: needle } : {}), limit: RUN_PAGE };
}

/** The pages read for one query, newest first; the next page reads before the last run already read. */
export function useRunPages(query: ImportBatchQuery) {
  return useInfiniteQuery({
    // Under "import-batches", so a run's apply or correction invalidates every page read.
    queryKey: ["import-batches", "register", query],
    queryFn: ({ pageParam }) => importsApi.listBatches(pageParam ? { ...query, before: pageParam } : query),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: ImportBatch[]) => last.length === (query.limit ?? RUN_PAGE) ? last.at(-1)?.id : undefined,
    placeholderData: keepPreviousData,
  });
}

export interface RunRegister {
  runs: ImportBatch[];
  /** The last page was full, so older runs may remain unread. */
  more: boolean;
  /** These are the previous query's runs, still drawn while the new query reads. */
  reading: boolean;
}

/**
 * The register's read. A new filter keeps the previous runs drawn, marked as
 * reading, until its own page arrives; a failed read of the new filter fails
 * rather than passing the old filter's runs off as its answer.
 */
export function runRegisterRead(pages: UseInfiniteQueryResult<InfiniteData<ImportBatch[]>>): Read<RunRegister> {
  const retry = () => { void pages.refetch(); };
  if (pages.data && pages.isPlaceholderData && !pages.isError)
    return { status: "ready", value: { runs: pages.data.pages.flat(), more: false, reading: true }, readAt: new Date(pages.dataUpdatedAt).toISOString() };
  if (pages.data && !pages.isPlaceholderData) {
    const value = { runs: pages.data.pages.flat(), more: pages.hasNextPage, reading: false };
    const readAt = new Date(pages.dataUpdatedAt).toISOString();
    return pages.isRefetchError
      ? { status: "stale", last: value, readAt, sentence: historyCopy.stale, refresh: retry }
      : { status: "ready", value, readAt };
  }
  return pages.isError ? { status: "failed", sentence: historyCopy.failed, retry } : { status: "loading" };
}
